export type ScheduleKind = "departure" | "estimated";

export type ScheduleTime = {
  time: string;
  kind: ScheduleKind;
};

export type BusStopKey =
  | "transfer"
  | "railway"
  | "fugangVillage"
  | "xiaoyeliu"
  | "xiadulan"
  | "jinzun"
  | "eastCoast"
  | "chenggong"
  | "sanxiantai"
  | "shiyusan";

export type BusScheduleRow = {
  route: string;
  destination: string;
  liveUrl: string;
  serviceNote?: string;
  origin?: string;
  originDeparture?: ScheduleTime;
  stops: Partial<Record<BusStopKey, ScheduleTime>>;
};

export const busStops: Array<{
  key: BusStopKey;
  zh: string;
  en: string;
  note?: string;
  featured?: boolean;
  extended?: boolean;
}> = [
  {
    key: "transfer",
    zh: "台東轉運站",
    en: "Taitung Bus Station",
    note: "鐵花村、市區",
  },
  {
    key: "railway",
    zh: "台東火車站",
    en: "Taitung Railway Station",
    featured: true,
  },
  {
    key: "fugangVillage",
    zh: "富岡新村",
    en: "Fugang Village",
    note: "綠島、蘭嶼",
  },
  { key: "xiaoyeliu", zh: "小野柳", en: "Xiaoyeliu" },
  {
    key: "xiadulan",
    zh: "下都蘭",
    en: "Xiadulan",
    note: "小星最近站",
    featured: true,
  },
  { key: "jinzun", zh: "金樽", en: "Jinzun", extended: true },
  {
    key: "eastCoast",
    zh: "東管處／都歷",
    en: "East Coast / Duli",
    note: "最美月光海",
    extended: true,
  },
  { key: "chenggong", zh: "成功", en: "Chenggong", extended: true },
  { key: "sanxiantai", zh: "三仙台", en: "Sanxiantai", extended: true },
  { key: "shiyusan", zh: "石雨傘", en: "Shiyusan", extended: true },
];

const routeUrl = (route: string) =>
  `https://www.taiwanbus.tw/eBUSPage/Query/QueryResult.aspx?rno=${route}0`;

function addMinutes(time: string, minutes: number) {
  const [hours, mins] = time.split(":").map(Number);
  const total = hours * 60 + mins + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

const estimated = (time: string, offset: number): ScheduleTime => ({
  time: addMinutes(time, offset),
  kind: "estimated",
});

const departure = (time: string): ScheduleTime => ({
  time,
  kind: "departure",
});

type OutboundRoute = {
  route: string;
  destination: string;
  departures: string[];
  profile: "coast" | "bridge";
  reaches: "jinzun" | "chenggong" | "shiyusan";
};

const outboundRoutes: OutboundRoute[] = [
  {
    route: "8119",
    destination: "花蓮轉運站",
    departures: ["06:30"],
    profile: "bridge",
    reaches: "shiyusan",
  },
  {
    route: "8102",
    destination: "德蕾莎（經中華大橋）",
    departures: ["07:00", "08:40", "14:40", "16:50"],
    profile: "bridge",
    reaches: "shiyusan",
  },
  {
    route: "8101",
    destination: "德蕾莎",
    departures: ["07:40", "12:20", "17:30"],
    profile: "coast",
    reaches: "shiyusan",
  },
  {
    route: "8103",
    destination: "成功",
    departures: ["09:40", "11:30", "13:30", "15:20", "19:00", "20:50"],
    profile: "coast",
    reaches: "chenggong",
  },
  {
    route: "8109",
    destination: "泰源",
    departures: ["10:30", "14:20", "16:00", "18:30"],
    profile: "coast",
    reaches: "jinzun",
  },
  {
    route: "8120",
    destination: "成功（經中華大橋）",
    departures: ["10:40", "16:10", "18:10"],
    profile: "bridge",
    reaches: "chenggong",
  },
];

function createOutboundRow(route: OutboundRoute, time: string): BusScheduleRow {
  const xiadulanOffset = route.profile === "coast" ? 55 : 31;
  const stops: BusScheduleRow["stops"] = {
    transfer: departure(time),
    fugangVillage: estimated(time, xiadulanOffset - 13),
    xiaoyeliu: estimated(time, xiadulanOffset - 11),
    xiadulan: estimated(time, xiadulanOffset),
  };

  if (route.profile === "coast") stops.railway = estimated(time, 22);

  stops.jinzun = estimated(time, xiadulanOffset + 16);
  if (route.reaches !== "jinzun") {
    stops.eastCoast = estimated(time, xiadulanOffset + 28);
    stops.chenggong = estimated(time, xiadulanOffset + 47);
  }
  if (route.reaches === "shiyusan") {
    stops.sanxiantai = estimated(time, xiadulanOffset + 60);
    stops.shiyusan = estimated(time, xiadulanOffset + 69);
  }

  return {
    route: route.route,
    destination: route.destination,
    liveUrl: routeUrl(route.route),
    stops,
  };
}

export const outboundBusSchedule = outboundRoutes
  .flatMap((route) =>
    route.departures.map((time) => createOutboundRow(route, time)),
  )
  .sort((a, b) =>
    (a.stops.transfer?.time ?? "").localeCompare(b.stops.transfer?.time ?? ""),
  );

type ReturnTrip = {
  route: string;
  destination: string;
  origin: string;
  originDeparture: string;
  xiadulan: string;
  passesRailway: boolean;
  serviceNote?: string;
};

const returnTrips: ReturnTrip[] = [
  {
    route: "8122",
    destination: "台東保養廠",
    origin: "隆昌",
    originDeparture: "06:40",
    xiadulan: "07:10",
    passesRailway: false,
    serviceNote: "僅平日行駛；寒暑假期間停駛",
  },
  {
    route: "8103",
    destination: "台東轉運站",
    origin: "成功",
    originDeparture: "06:30",
    xiadulan: "07:16",
    passesRailway: true,
  },
  {
    route: "8120",
    destination: "台東轉運站",
    origin: "成功",
    originDeparture: "07:00",
    xiadulan: "07:50",
    passesRailway: false,
  },
  {
    route: "8109",
    destination: "台東轉運站",
    origin: "泰源",
    originDeparture: "08:00",
    xiadulan: "08:26",
    passesRailway: true,
  },
  {
    route: "8102",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "06:30",
    xiadulan: "08:35",
    passesRailway: false,
  },
  {
    route: "8102",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "07:30",
    xiadulan: "09:35",
    passesRailway: false,
  },
  {
    route: "8101",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "08:20",
    xiadulan: "10:21",
    passesRailway: true,
  },
  {
    route: "8101",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "09:10",
    xiadulan: "11:11",
    passesRailway: true,
  },
  {
    route: "8102",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "10:00",
    xiadulan: "12:05",
    passesRailway: false,
  },
  {
    route: "8103",
    destination: "台東轉運站",
    origin: "成功",
    originDeparture: "12:05",
    xiadulan: "12:51",
    passesRailway: true,
  },
  {
    route: "8109",
    destination: "台東轉運站",
    origin: "泰源",
    originDeparture: "12:30",
    xiadulan: "12:56",
    passesRailway: true,
  },
  {
    route: "8103",
    destination: "台東轉運站",
    origin: "成功",
    originDeparture: "12:50",
    xiadulan: "13:36",
    passesRailway: true,
  },
  {
    route: "8101",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "12:15",
    xiadulan: "14:16",
    passesRailway: true,
  },
  {
    route: "8102",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "13:25",
    xiadulan: "15:30",
    passesRailway: false,
  },
  {
    route: "8101",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "14:25",
    xiadulan: "16:26",
    passesRailway: true,
  },
  {
    route: "8119",
    destination: "台東轉運站",
    origin: "花蓮轉運站",
    originDeparture: "13:30",
    xiadulan: "17:40",
    passesRailway: false,
  },
  {
    route: "8109",
    destination: "台東轉運站",
    origin: "泰源",
    originDeparture: "17:30",
    xiadulan: "17:56",
    passesRailway: true,
  },
  {
    route: "8101",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "16:05",
    xiadulan: "18:06",
    passesRailway: true,
  },
  {
    route: "8102",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "16:45",
    xiadulan: "18:50",
    passesRailway: false,
  },
  {
    route: "8103",
    destination: "台東轉運站",
    origin: "成功",
    originDeparture: "18:45",
    xiadulan: "19:31",
    passesRailway: true,
  },
  {
    route: "8102",
    destination: "台東轉運站",
    origin: "德蕾莎",
    originDeparture: "18:00",
    xiadulan: "20:05",
    passesRailway: false,
  },
  {
    route: "8120",
    destination: "台東轉運站",
    origin: "成功",
    originDeparture: "19:45",
    xiadulan: "20:35",
    passesRailway: false,
  },
];

const returnReachByRoute: Record<
  string,
  OutboundRoute["reaches"] | "xiadulan"
> = {
  "8122": "xiadulan",
  "8109": "jinzun",
  "8103": "chenggong",
  "8120": "chenggong",
  "8101": "shiyusan",
  "8102": "shiyusan",
  "8119": "shiyusan",
};

export const returnBusSchedule: BusScheduleRow[] = returnTrips.map((trip) => {
  const reaches = returnReachByRoute[trip.route];
  const stops: BusScheduleRow["stops"] = {
    xiadulan: { time: trip.xiadulan, kind: "estimated" },
    xiaoyeliu: estimated(trip.xiadulan, 11),
    fugangVillage: estimated(trip.xiadulan, 13),
    transfer: estimated(trip.xiadulan, trip.passesRailway ? 55 : 31),
  };
  if (trip.passesRailway) stops.railway = estimated(trip.xiadulan, 32);
  if (reaches !== "xiadulan") {
    stops.jinzun = estimated(trip.xiadulan, -16);
  }
  if (reaches === "chenggong" || reaches === "shiyusan") {
    stops.eastCoast = estimated(trip.xiadulan, -28);
    stops.chenggong =
      trip.origin === "成功"
        ? departure(trip.originDeparture)
        : estimated(trip.xiadulan, -47);
  }
  if (reaches === "shiyusan") {
    stops.sanxiantai = estimated(trip.xiadulan, -60);
    stops.shiyusan = estimated(trip.xiadulan, -69);
  }

  return {
    route: trip.route,
    destination: trip.destination,
    liveUrl: routeUrl(trip.route),
    origin: trip.origin,
    originDeparture: departure(trip.originDeparture),
    serviceNote: trip.serviceNote,
    stops,
  };
});

export const scheduleVerifiedOn = "2026/08/26";
